import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useSpring } from "framer-motion";
import { Link } from "react-router-dom";

function Main() {
	const roles = ["Software Developer", "Full Stack Engineer"];
	const [currentRole, setCurrentRole] = useState(0);

	const mouseX = useSpring(0, { damping: 25, stiffness: 150 });
	const mouseY = useSpring(0, { damping: 25, stiffness: 150 });

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentRole((prev) => (prev + 1) % roles.length);
		}, 3000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		const handleMouseMove = (e) => {
			mouseX.set(e.clientX - 192);
			mouseY.set(e.clientY - 192);
		};
		window.addEventListener("mousemove", handleMouseMove);
		return () => window.removeEventListener("mousemove", handleMouseMove);
	}, [mouseX, mouseY]);

	const scrollToContent = () => {
		window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
	};

	return (
		<div className="min-h-screen relative overflow-hidden pt-16">
			{/* Subtle silver/slate glow that drifts */}
			<motion.div
				className="absolute inset-0 opacity-15 pointer-events-none"
				animate={{
					background: [
						"radial-gradient(circle at 20% 50%, rgba(148,163,184,0.3) 0%, transparent 55%)",
						"radial-gradient(circle at 80% 80%, rgba(148,163,184,0.3) 0%, transparent 55%)",
						"radial-gradient(circle at 40% 20%, rgba(148,163,184,0.3) 0%, transparent 55%)"
					]
				}}
				transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
			/>

			{/* Mouse follow — slate glow */}
			<motion.div
				className="absolute w-64 h-64 md:w-96 md:h-96 rounded-full bg-slate-400 opacity-10 blur-3xl pointer-events-none hidden md:block will-change-transform"
				style={{ x: mouseX, y: mouseY }}
			/>

			<div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					className="text-center w-full max-w-5xl"
				>
					<motion.div
						initial={{ scale: 0.5, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ duration: 0.5 }}
						className="mb-4 sm:mb-6"
					>
						<span className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-white/8 backdrop-blur-sm border border-white/20 rounded-full text-slate-300 text-xs sm:text-sm font-medium">
							Welcome to my portfolio
						</span>
					</motion.div>

					<h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent px-4">
						Matteo Robidoux
					</h1>

					<div className="h-16 sm:h-20 mb-6 sm:mb-8">
						<AnimatePresence mode="wait">
							<motion.h2
								key={currentRole}
								initial={{ y: 20, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								exit={{ y: -20, opacity: 0 }}
								transition={{ duration: 0.5 }}
								className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-slate-300 font-light px-4"
							>
								{roles[currentRole]}
							</motion.h2>
						</AnimatePresence>
					</div>

					<motion.p
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.5, duration: 0.8 }}
						className="text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-8 sm:mb-12 px-4"
					>
						Creating innovative software solutions with a passion for technology
						and learning.
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.8, duration: 0.5 }}
						className="flex gap-3 sm:gap-4 justify-center flex-wrap px-4"
					>
						<Link to="/portfolio">
							<button className="px-6 py-3 sm:px-8 sm:py-4 bg-white rounded-lg text-slate-900 text-sm sm:text-base font-semibold hover:shadow-lg hover:shadow-white/20 hover:bg-slate-100 transition-all duration-300 hover:scale-105 active:scale-95">
								View My Work
							</button>
						</Link>
						<Link to="/contact">
							<button className="px-6 py-3 sm:px-8 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white text-sm sm:text-base font-semibold hover:bg-white/20 transition-all duration-300 active:scale-95">
								Get In Touch
							</button>
						</Link>
					</motion.div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 1.2, duration: 0.8 }}
						className="mt-12 sm:mt-20"
					>
						<motion.button
							onClick={scrollToContent}
							animate={{ y: [0, 10, 0] }}
							transition={{ duration: 2, repeat: Infinity }}
							className="text-slate-400 hover:text-slate-200 cursor-pointer transition-colors"
							aria-label="Scroll down"
						>
							<svg
								className="w-5 h-5 sm:w-6 sm:h-6 mx-auto"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M19 14l-7 7m0 0l-7-7m7 7V3"
								/>
							</svg>
						</motion.button>
					</motion.div>
				</motion.div>
			</div>
		</div>
	);
}

export default Main;
